import { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import Modal from '../../components/ui/Modal';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Bot, Plus, Edit2, Trash2, Send, Wifi, WifiOff, MessageCircle, ShieldCheck, Bell, X } from 'lucide-react';

const ROLE_OPTIONS = [
  { value: 'REVIEWER', label: 'Reviewer (Deposits/Withdrawals with Approve/Reject buttons)', icon: ShieldCheck },
  { value: 'HUB', label: 'Hub (Notification-only alerts, no action buttons)', icon: Bell },
];

const CATEGORY_OPTIONS = [
  { value: 'payments', label: 'Deposits' },
  { value: 'withdrawals', label: 'Withdrawals' },
  { value: 'orders', label: 'Game Orders' },
  { value: 'users', label: 'User Events' },
  { value: 'system', label: 'System Alerts' },
  { value: 'reviews', label: 'Reviews' },
];

export default function TelegramConfigPage() {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [testing, setTesting] = useState(null);

  const defaultForm = {
    name: '', role: 'REVIEWER', bot_token: '', chat_id: '',
    description: '', enabled: true, filters: { notification_categories: [], severity_levels: ['info', 'warning', 'error', 'critical'] },
  };
  const [form, setForm] = useState(defaultForm);

  useEffect(() => { fetchTargets(); }, []);

  const fetchTargets = async () => {
    try {
      const res = await api.get('/api/admin/telegram-targets');
      setTargets(res.data.targets || []);
    } catch { toast.error('Failed to load telegram targets'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.bot_token) delete payload.bot_token;
        await api.patch(`/api/admin/telegram-targets/${editing.id}`, payload);
        toast.success('Bot updated');
      } else {
        if (!form.bot_token || !form.chat_id) {
          toast.error('Bot token and Chat ID are required');
          setSubmitting(false);
          return;
        }
        await api.post('/api/admin/telegram-targets', form);
        toast.success('Telegram bot added!');
      }
      setShowForm(false);
      setEditing(null);
      setForm(defaultForm);
      fetchTargets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this Telegram bot?')) return;
    try {
      await api.delete(`/api/admin/telegram-targets/${id}`);
      toast.success('Bot removed');
      fetchTargets();
    } catch { toast.error('Failed to delete'); }
  };

  const handleTest = async (id) => {
    setTesting(id);
    try {
      const res = await api.post(`/api/admin/telegram-targets/${id}/test`);
      if (res.data.success) {
        toast.success('Test message sent! Check your Telegram.');
      } else {
        toast.error(res.data.message || 'Test failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test failed');
    } finally { setTesting(null); }
  };

  const handleToggle = async (target) => {
    try {
      await api.patch(`/api/admin/telegram-targets/${target.id}`, { enabled: !target.enabled });
      toast.success(target.enabled ? 'Bot disabled' : 'Bot enabled');
      fetchTargets();
    } catch { toast.error('Failed'); }
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name || '', role: t.role || 'REVIEWER', bot_token: '',
      chat_id: t.chat_id || '', description: t.description || '',
      enabled: t.enabled !== false,
      filters: t.filters || { notification_categories: [], severity_levels: [] },
    });
    setShowForm(true);
  };

  const toggleCategory = (cat) => {
    setForm(p => {
      const cats = p.filters?.notification_categories || [];
      const updated = cats.includes(cat) ? cats.filter(c => c !== cat) : [...cats, cat];
      return { ...p, filters: { ...p.filters, notification_categories: updated } };
    });
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="telegram-config-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Bot className="w-5 h-5 text-[#D4AF37]" /> Telegram Bots
          </h2>
          <p className="text-sm text-gray-400">Configure Telegram bots for deposit/withdrawal/activity alerts</p>
        </div>
        <Button onClick={() => { setForm(defaultForm); setEditing(null); setShowForm(true); }} className="bg-[#D4AF37] text-black" data-testid="add-telegram-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Bot
        </Button>
      </div>

      {/* Bot List */}
      <div className="space-y-2">
        {targets.map(t => (
          <div key={t.id} className={`glass-card rounded-xl p-4 ${!t.enabled ? 'opacity-50' : ''}`} data-testid={`telegram-target-${t.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${t.role === 'REVIEWER' ? 'bg-purple-500/20' : 'bg-blue-500/20'}`}>
                  {t.role === 'REVIEWER' ? <ShieldCheck className="w-5 h-5 text-purple-400" /> : <Bell className="w-5 h-5 text-blue-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-sm truncate">{t.name}</h3>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${t.role === 'REVIEWER' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'}`}>
                      {t.role}
                    </span>
                    {t.token_status === 'valid' ? (
                      <Wifi className="w-3 h-3 text-green-400" />
                    ) : (
                      <WifiOff className="w-3 h-3 text-red-400" />
                    )}
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${t.enabled ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                      {t.enabled ? 'ACTIVE' : 'OFF'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">Chat: {t.chat_id} {t.description ? `| ${t.description}` : ''}</p>
                  {t.filters?.notification_categories?.length > 0 && (
                    <div className="flex gap-1 mt-1">
                      {t.filters.notification_categories.map(c => (
                        <span key={c} className="text-[9px] bg-white/5 px-1.5 py-0.5 rounded">{c}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 ml-2">
                <Button size="sm" variant="ghost" onClick={() => handleTest(t.id)} disabled={testing === t.id}
                  className="text-xs h-7" data-testid={`test-telegram-${t.id}`}>
                  <Send className={`w-3 h-3 ${testing === t.id ? 'animate-pulse' : ''}`} />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleToggle(t)} className="text-xs h-7">
                  {t.enabled ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => openEdit(t)} className="text-xs h-7"><Edit2 className="w-3 h-3" /></Button>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(t.id)} className="text-xs h-7 text-red-400"><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          </div>
        ))}
        {targets.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Bot className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>No Telegram bots configured. Add one to receive alerts.</p>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal open={showForm} onClose={() => setShowForm(false)} title={editing ? 'Edit Telegram Bot' : 'Add Telegram Bot'} maxWidth="max-w-lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
            placeholder="Bot Name (e.g. Deposit Alerts)" className="bg-[#0B0E14] border-white/10" required data-testid="tg-name" />

          {/* Role Selector */}
          <div>
            <label className="text-xs text-gray-400 mb-2 block">Bot Role</label>
            <div className="space-y-1.5">
              {ROLE_OPTIONS.map(r => (
                <button key={r.value} type="button" onClick={() => setForm(p => ({ ...p, role: r.value }))}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg text-left text-xs transition-colors ${form.role === r.value ? 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/50' : 'bg-[#0B0E14] hover:bg-white/5 text-gray-400 border border-white/5'}`}>
                  <r.icon className="w-5 h-5 flex-shrink-0" />
                  <span>{r.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-400 mb-1 block">Bot Token {editing && '(leave empty to keep current)'}</label>
            <Input value={form.bot_token} onChange={e => setForm(p => ({ ...p, bot_token: e.target.value }))}
              placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11" className="bg-[#0B0E14] border-white/10 font-mono text-xs"
              required={!editing} data-testid="tg-token" />
          </div>

          <div>
            <label className="text-xs text-gray-400 mb-1 block">Chat ID</label>
            <Input value={form.chat_id} onChange={e => setForm(p => ({ ...p, chat_id: e.target.value }))}
              placeholder="-1001234567890" className="bg-[#0B0E14] border-white/10 font-mono"
              required data-testid="tg-chatid" />
            <p className="text-[10px] text-gray-500 mt-0.5">Group/channel chat ID. Use @userinfobot to find it.</p>
          </div>

          <Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            placeholder="Description (optional)" className="bg-[#0B0E14] border-white/10" />

          {/* Notification Categories (for HUB) */}
          {form.role === 'HUB' && (
            <div>
              <label className="text-xs text-gray-400 mb-2 block">Notification Categories</label>
              <div className="grid grid-cols-2 gap-1.5 bg-[#0B0E14] rounded-lg p-3">
                {CATEGORY_OPTIONS.map(c => (
                  <label key={c.value} className="flex items-center gap-2 cursor-pointer text-xs py-1">
                    <input type="checkbox" checked={(form.filters?.notification_categories || []).includes(c.value)}
                      onChange={() => toggleCategory(c.value)} className="w-3.5 h-3.5 rounded accent-[#D4AF37]" />
                    <span className="text-gray-300">{c.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input type="checkbox" checked={form.enabled} onChange={e => setForm(p => ({ ...p, enabled: e.target.checked }))}
              className="w-4 h-4 rounded accent-[#D4AF37]" />
            <span className="text-gray-300">Enabled</span>
          </label>

          <div className="flex justify-end gap-3 pt-2 border-t border-white/5">
            <Button type="button" onClick={() => setShowForm(false)} variant="outline" className="border-white/10">Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-[#D4AF37] text-black" data-testid="tg-submit">
              {submitting ? 'Saving...' : editing ? 'Update Bot' : 'Add Bot'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
